import { useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Badge } from "@/components/ui/badge";
import {
  useAttachContextSource,
  useContextSource,
  useContextSources,
  useDetachContextSource,
  useRefreshContextSourceCapabilities,
} from "@/hooks/agent/useContextSource";
import { Agent } from "@/types/agent/agent";

interface PrivateContextSourceCardProps {
  agent: Agent;
  brand?: string;
}

const SUPPORTED_SOURCE_TYPES = ["text_document", "api", "mcp"] as const;
type SupportedSourceType = (typeof SUPPORTED_SOURCE_TYPES)[number];

const isSupportedSourceType = (type?: string | null): type is SupportedSourceType =>
  !!type && SUPPORTED_SOURCE_TYPES.includes(type as SupportedSourceType);

const getTypeLabel = (sourceType: string): string => {
  if (sourceType === "text_document") return "DOC";
  if (sourceType === "api") return "API";
  if (sourceType === "mcp") return "MCP";
  return sourceType.toUpperCase();
};

const getDisplayName = (source: any): string => {
  const url = source.source_type === "mcp" ? source.config?.server_url || "" : source.config?.base_url || "";
  return url.replace(/^https?:\/\//, "").split("/")[0] || url;
};

export const PrivateContextSourceCard = ({ agent, brand = "#4E9CFF" }: PrivateContextSourceCardProps) => {
  const agentId = agent?.id;
  const [showDropdown, setShowDropdown] = useState(false);

  const { data: agentSource, isLoading: isLoadingSource } = useContextSource(agentId);
  const { data: userSourcesData, isLoading: isLoadingSources } = useContextSources();
  const { mutate: attachSource, isPending: isAttaching } = useAttachContextSource();
  const { mutate: detachSource, isPending: isDeleting } = useDetachContextSource();
  const { mutate: refreshCapabilities, isPending: isRefreshing } = useRefreshContextSourceCapabilities();

  if (!agentId) return null;

  const nestedData =
    userSourcesData && typeof userSourcesData === "object" && "data" in userSourcesData
      ? (userSourcesData as any).data
      : undefined;
  const userSources: any[] = Array.isArray(userSourcesData)
    ? userSourcesData
    : Array.isArray(nestedData)
      ? nestedData
      : [];

  const supportedUserSources = userSources.filter((s) => isSupportedSourceType(s.source_type));
  const supportedAgentSource = agentSource && isSupportedSourceType(agentSource.source_type) ? agentSource : null;
  const isSourceAttachedToAgent = (sourceId: number) =>
    userSources.find((s) => s.id === sourceId)?.agents?.some((a: any) => a.agent_id === agentId) || false;

  const handleAttach = (sourceId: number) => {
    attachSource(
      { agentId, sourceId },
      {
        onSuccess: () => {
          setShowDropdown(false);
          toast.success("Integration attached successfully");
        },
        onError: (error: Error) => toast.error(`Error: ${error.message}`),
      },
    );
  };

  const handleDetach = (sourceId?: number) => {
    if (!confirm("Are you sure you want to detach this integration?")) return;
    detachSource(
      { agentId, sourceId },
      {
        onSuccess: () => toast.success("Integration detached successfully"),
        onError: (error: Error) => toast.error(`Error: ${error.message}`),
      },
    );
  };

  const handleRefresh = () => {
    refreshCapabilities(agentId, {
      onError: (error: Error) => toast.error(`Error: ${error.message}`),
    });
  };

  const isLoading = isLoadingSource || isLoadingSources;

  const sourceDropdown = (
    <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
      {supportedUserSources.map((source) => {
        const isAttached = isSourceAttachedToAgent(source.id);
        return (
          <div
            key={source.id}
            className="flex items-center justify-between px-4 py-3 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors text-sm"
          >
            <button
              type="button"
              onClick={() => !isAttached && handleAttach(source.id)}
              disabled={isAttaching || isAttached}
              className="flex-1 text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs flex-shrink-0 border-gray-300 text-gray-600">
                  {getTypeLabel(source.source_type)}
                </Badge>
                <span className="font-medium text-gray-900">{getDisplayName(source)}</span>
              </div>
              {isAttached && <p className="text-xs text-gray-500 mt-1">Attached to this agent</p>}
            </button>
            {isAttached && (
              <button
                type="button"
                onClick={() => handleDetach(source.id)}
                disabled={isDeleting}
                className="ml-2 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50 flex-shrink-0"
              >
                Detach
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <OverviewSectionCard
      title="Private Context Source"
      subtitle="Attach a configured integration for your agent to search"
      genieIcon="lock"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : supportedUserSources.length === 0 ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900 font-medium">No integrations configured</p>
            <p className="text-xs text-blue-700 mt-1">
              Go to{" "}
              <a href="/genie-hub/integrations" className="font-semibold hover:underline">
                Integrations
              </a>{" "}
              to set up your first data source
            </p>
          </div>
        ) : !supportedAgentSource ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Select an integration to attach to this agent:</p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-left text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <span>Choose integration...</span>
                <span className="text-xs text-gray-400">▼</span>
              </button>
              {showDropdown && sourceDropdown}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm font-medium text-green-900">Integration attached</p>
              <p className="text-xs text-green-700 mt-1">{getDisplayName(supportedAgentSource)}</p>
              {supportedAgentSource.capabilities?.resources?.length > 0 && (
                <div className="mt-3 text-xs text-green-700">
                  <p className="font-medium mb-1">Available resources:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    {supportedAgentSource.capabilities.resources.slice(0, 3).map((res: any, idx: number) => (
                      <li key={idx}>{res.name}</li>
                    ))}
                    {supportedAgentSource.capabilities.resources.length > 3 && (
                      <li>... and {supportedAgentSource.capabilities.resources.length - 3} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Change
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {isRefreshing ? "Refreshing..." : "Refresh"}
              </button>
              <button
                type="button"
                onClick={() => handleDetach()}
                disabled={isDeleting}
                className="flex-1 px-3 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                {isDeleting ? "Detaching..." : "Detach"}
              </button>
            </div>
            {showDropdown && sourceDropdown}
            <a
              href="/genie-hub/integrations"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              Manage integrations
            </a>
          </div>
        )}
      </div>
    </OverviewSectionCard>
  );
};
